import {
    AuthenticationContext,
    oidcContext,
} from '@axa-fr/react-oidc-context/dist/oidcContext/AuthenticationContext'
import React, { useContext, useState, useEffect } from 'react'

export interface OidcUserContext extends oidcContext {
    hasAttemptedLoading: boolean
    userJustExpired: boolean
}

// Reason for disable: we check for undefined where it's used. Annoying to provide defaults
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const UserContext = React.createContext<OidcUserContext>(undefined!)

export function UserProvider({ children }: React.PropsWithChildren<any>) {
    const authContext = React.useContext(AuthenticationContext)

    if (authContext === undefined) {
        throw new Error(
            'UserProvider must be used within an AuthenticationProvider',
        )
    }

    // `authContext.isLoading` starts as false.
    // we need to track whether loading has been attempted or not,
    // otherwise we can't tell if you're "not logged in" or we haven't checked yet.
    // for more info, see `check-profile.tsx`
    const [hasAttemptedLoading, setHasAttemptedLoading] = useState(
        authContext.isLoading,
    )

    // we need to know whether we're in the 'expired' user flow
    // if so, `oidcUser` will be cleared, but it will come back
    const userIsExpired = authContext.oidcUser?.expired
    const [userJustExpired, setHasJustExpired] = useState(
        userIsExpired ?? false,
    )

    useEffect(() => {
        if (authContext.isLoading) {
            setHasAttemptedLoading(true)
        }
    }, [authContext.isLoading])

    useEffect(() => {
        // do nothing if there's no user. If there was never a user, we initialised to false
        // if there was an expired user, now no user, we're not ready to reset the flag
        if (userIsExpired === undefined) {
            return
        }
        setHasJustExpired(userIsExpired)
    }, [userIsExpired])

    return (
        <UserContext.Provider
            value={{ ...authContext, hasAttemptedLoading, userJustExpired }}
        >
            {children}
        </UserContext.Provider>
    )
}

export function useUser(): OidcUserContext {
    const context = useContext(UserContext)

    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider')
    }

    return context
}
